import { useEffect } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import { useStateContext } from "contexts/ContextProvider";
import { useTheme } from "@mui/material";

/**
 * Hook to listen for assignment updates and trigger refetch + notification
 * @param {Object} params
 * @param {string} params.modelType - Type of model (e.g., "Interpretation", "Translation", "OtherService")
 * @param {number} params.recordId - ID of the assignment/record
 * @param {Function} params.onUpdate - Callback function to refetch data (called when update is received, receives updatedBy object)
 * @param {Function} params.toastFn - Toast notification function (optional, for custom toast handling)
 * @param {Array<number>} params.visibleRecordIds - IDs currently visible in the filtered list. When provided,
 *   updates for records NOT in this set are ignored (no refetch, no toast) — a change to a May assignment
 *   doesn't reload a list currently filtered to August. Empty/undefined = always refresh (backward compatible).
 * @returns {void}
 */
export const useAssignmentUpdates = ({
  modelType,
  recordId,
  onUpdate,
  toastFn,
  visibleRecordIds,
}) => {
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();
  const { showSnackbar } = useStateContext();
  const theme = useTheme();

  useEffect(() => {
    if (!socket || !modelType || !recordId || !onUpdate) {
      return;
    }

    // Handler for assignment updates
    const handleAssignmentUpdate = (payload = {}) => {
      // Normalize various event payload shapes (assignment:updated, service:updated, etc.)
      const updatedBy = payload.updatedBy || payload.updated_by || payload.updater || null;
      const updateModelType = payload.modelType || payload.model_type || payload.model || payload.event || modelType;
      // Prefer the concrete service id when present (list broadcasts carry
      // recordId "all" + serviceId <id>); fall back to recordId.
      const updateRecordId =
        payload.serviceId ??
        payload.service_id ??
        payload.recordId ??
        payload.record_id ??
        payload.record ??
        null;
      const projectNumber = payload.projectNumber || payload.project_number || null;
      const changeType = payload.changeType || payload.change_type || payload.event || null;
      const changeAction = payload.changeAction || payload.change_action || null;

      if (!updateRecordId) return;

      // List pages register with recordId "all" and receive "all" broadcasts
      // (e.g. { modelType: "InterpretationsList", recordId: "all" }). Since
      // Number("all") is NaN, numeric comparison alone never matches — treat
      // "all" on either side as a wildcard match (same pattern as useEntityUpdates).
      const isAllRecordsMatch = recordId === "all" || updateRecordId === "all";
      const recordIdNumberMatch = Number(updateRecordId) === Number(recordId);

      // Only process if this is for our current assignment (modelType match is optional/lenient)
      if (isAllRecordsMatch || recordIdNumberMatch) {
        // Filter-scoped refresh: when the list tells us which records are
        // currently visible, skip updates for records outside that set — a
        // change to a May assignment must not reload a list filtered to August.
        // Only applies when we have a CONCRETE numeric id ("all" broadcasts
        // without a serviceId fall through and always refresh).
        const numericUpdateId = Number(updateRecordId);
        const hasConcreteId =
          updateRecordId !== "all" && Number.isFinite(numericUpdateId);
        if (
          hasConcreteId &&
          Array.isArray(visibleRecordIds) &&
          visibleRecordIds.length > 0 &&
          !visibleRecordIds.includes(numericUpdateId)
        ) {
          return;
        }

        // Self-edit detection: suppress the TOAST for your own edits, but still
        // refetch — another tab/window of the same user receives the broadcast
        // (the server excludes only the sender's socket) and its data is stale.
        const isSelfEdit =
          updatedBy && updatedBy.userId && updatedBy.userId === currentUser?.id;

        const meta = {
          projectNumber,
          changeType,
          changeAction,
          modelType: updateModelType,
          recordId: updateRecordId,
        };

        const notifierName = (updatedBy && (updatedBy.userName || updatedBy.name)) || payload.actorName || payload.actor || "Another member";

        // Show toast/notification (skipped for own edits)
        if (!isSelfEdit) {
          if (toastFn) {
            toastFn(`${notifierName} updated the assignment. Check History for more details.`);
          } else if (showSnackbar) {
            showSnackbar(`${notifierName} updated the assignment. Check History for more details.`, theme, "info");
          }
        }

        // Trigger refetch with updater info
        onUpdate(updatedBy || { userName: notifierName, userId: null }, meta);
      }
    };

    // Listen for assignment:updated events and legacy/service updates
    socket.on("assignment:updated", handleAssignmentUpdate);
    socket.on("service:updated", handleAssignmentUpdate);

    // Cleanup
    return () => {
      socket.off("assignment:updated", handleAssignmentUpdate);
      socket.off("service:updated", handleAssignmentUpdate);
    };
  }, [
    socket,
    modelType,
    recordId,
    onUpdate,
    currentUser?.id,
    showSnackbar,
    theme,
    toastFn,
    visibleRecordIds,
  ]);
};

export default useAssignmentUpdates;
